import { MutationCache, QueryClient } from '@tanstack/react-query'

import { isApiError } from '@/shared/api/errors'
import { log } from '@/shared/lib/log'

/**
 * Cliente de React Query.
 *
 * **La cache NO se persiste en localStorage.** Los accesos, las notas internas
 * y los briefs no van a disco: en una maquina compartida eso es una fuga que no
 * se ve venir. El costo es un refetch al recargar; es barato.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    /**
     * Toda mutación que falla queda registrada, sin que cada hook se acuerde.
     *
     * El `mutationKey` dice QUÉ se intentaba (`['tasks','update']`), el
     * `request_id` ata el intento con la traza del servidor, y las variables
     * dicen sobre qué. El logger redacta secretos y tokens antes de emitir, así
     * que el cuerpo de un alta de acceso no se filtra por acá.
     */
    mutationCache: new MutationCache({
      onError: (error, variables, _context, mutation) => {
        log.error('mutation_failed', {
          mutation: mutation.options.mutationKey ?? 'sin-key',
          status: isApiError(error) ? error.status : 0,
          code: isApiError(error) ? error.code : 'unknown_error',
          request_id: isApiError(error) ? error.requestId : null,
          message: error instanceof Error ? error.message : String(error),
          variables,
        })
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        // Las pantallas de orden lo apagan aparte: un refetch al volver el foco
        // con un drop en vuelo pisa la cache y produce el salto visual.
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
          if (isApiError(error)) {
            // El interceptor ya resolvio el 401. 403/404/409/422 son decisiones
            // del backend: reintentar solo agrega ruido.
            if (!error.isRetryable) return false
          }
          return failureCount < 2
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      },
      mutations: {
        retry: false,
      },
    },
  })
}
